import { useEffect, useRef } from 'react'

import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { useIsomorphicLayoutEffect } from 'react-use'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  StoreFrontDto,
  StoreFrontOptionsDefaultViewEnum,
} from '@b-stock/listing-api-client'
import type { AllListingsFilters } from '@b-stock/search-api-client'

import { isBidCardTemplateEnabled } from '@/components/listings/BidCard/isBidCardTemplateEnabled'
import { FeatureFlags } from '@/helpers/featureFlags'
import stableStringify from '@/helpers/stableStringify'

import { useSavedSearch } from '../savedSearch/SavedSearchContext'
import useSavedSearchId, {
  deleteSavedSearchId,
} from '../savedSearch/useSavedSearchId'
import { defaultState, getView } from './defaultState'
import { readRememberedPageSize } from './rememberedPageSize'
import type { SearchAction } from './stateActions'
import { isSupportedQueryParam } from './typeInspectors'
import type { ListingSearchState } from './types'
import {
  listingSearchStateToUrlParams,
  unformatFilters,
  urlParamsToListingSearchState,
} from './utils'

const FILTER_KEYS = Object.keys(defaultState.filters)

/**
 * This hook maintains consistency between query params and the internal
 * search state. It watches both the state and the set of query params, and
 * updates the other when one of them changes. When the state changes, a
 * shallow navigation is performed, meaning we don't go through next's data
 * loading logic, and a new entry is entered in the browser history. When
 * the url changes (e.g. the user hits the back button) we update the state
 * triggering a re-query (possibly hitting react-query's cache). The use of
 * `stableStringify` is significant here because both state and `router.query`
 * can change object identity without a change in any value.
 */
export const useListingStateURLRectification = ({
  state,
  dispatch,
  allFilters,
  pageDefaultView,
  storefront,
  preserveIdInUrl = true,
  isPageSizeRemembered = false,
}: {
  state: ListingSearchState
  dispatch: (action: SearchAction) => void
  allFilters?: AllListingsFilters
  pageDefaultView?: StoreFrontOptionsDefaultViewEnum
  storefront?: StoreFrontDto
  // When false, `state.id` is never written to the URL and any existing `id`
  // param on the URL is not preserved. Used by the Watchlist page, where the
  // id allowlist is derived server-side from the buyer's watchlist and is
  // implicit to the /watchlist route — persisting it would replay a stale
  // snapshot on every bookmark and clutter the URL with listing ObjectIds.
  preserveIdInUrl?: boolean
  // A URL without `l` normally means the view's default page size; when true
  // it means the buyer's remembered page size instead.
  isPageSizeRemembered?: boolean
}) => {
  const router = useRouter()
  const enableMyBidsFilter = useFlag(
    FeatureFlags.ENABLE_MY_BIDS_LISTINGS_FILTER
  )
  const watchlistMyBidsTemplate = useFlag(
    FeatureFlags.ENABLE_WATCHLIST_MY_BIDS_TEMPLATE
  )
  const inlineBidBoxEnabled = useFlag(
    FeatureFlags.ENABLE_MY_BIDS_INLINE_BID_BOX
  )
  const useBidCardTemplate = isBidCardTemplateEnabled(
    watchlistMyBidsTemplate,
    inlineBidBoxEnabled
  )
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const isAppliedFromSavedSearch = useRef(false)
  const isInitialMount = useRef(true)
  const isInitialUrlSync = useRef(true)
  const prevQ = useRef<string | null>(null)
  const replaceNextNavigation = useRef(false)

  // Strip myBids everywhere (My Bids surfaces on /all-auctions and storefront
  // pages alike) when its flag is off, so a stale bookmark or a rollback
  // while a buyer has ?myBids=true open can't keep applying the filter.
  // Same reasoning for bidStatus: the Watchlist Bid Status filter (and its
  // dropdown + Clear filters affordance) is gated on the template flags.
  // With flags off, a crafted `?bidStatus=winning` URL would otherwise flip
  // isBidStatusFilterActive on and render the results row without any way
  // for the user to clear the filter.
  const getQueryParams = (params: URLSearchParams) => {
    const paramsObject = Object.fromEntries(params.entries())
    if (!enableMyBidsFilter) {
      delete paramsObject.myBids
    }
    if (!useBidCardTemplate) {
      delete paramsObject.bidStatus
      delete paramsObject.closesIn
    }
    return paramsObject
  }

  const { savedSearchId } = useSavedSearchId()
  const { currentSavedSearch } = useSavedSearch()

  // Extracted so it can appear as-is in the dep array below (the exhaustive-
  // deps lint rule warns about complex expressions like `searchParams?.toString()`
  // and can't statically verify the disable comment against a multi-line deps
  // array).
  const searchParamsString = searchParams?.toString()

  // A URL without `l` means the remembered page size, when there is one.
  const getRememberedPageSize = () =>
    isPageSizeRemembered && !searchParams?.has('l')
      ? readRememberedPageSize()
      : null

  // The URL-to-state effect below applies the same value, but only after
  // paint; this applies it before the first client paint so the buyer never
  // sees the default size flash. localStorage can't be read during SSR.
  useIsomorphicLayoutEffect(
    () => {
      const rememberedPageSize = getRememberedPageSize()
      if (
        rememberedPageSize !== null &&
        rememberedPageSize !== state.pageSize
      ) {
        replaceNextNavigation.current = true
        dispatch({ type: 'SET_PAGE_SIZE', pageSize: rememberedPageSize })
      }
    },
    // oxlint-disable-next-line react/exhaustive-deps
    []
  )

  // Rectify state when URL is changed.
  // IMPORTANT: This effect must be defined before the state-to-URL effect below.
  // On mount, React fires effects in definition order. This effect sets
  // replaceNextNavigation before state-to-URL reads it.
  useEffect(() => {
    const rememberedPageSize = getRememberedPageSize()

    if (searchParams?.toString()) {
      const q = searchParams.get('q')
      const s = searchParams.get('s')
      const o = searchParams.get('o')

      // When someone externally navigates to a URL with q but no sort params
      // (e.g. clicking a Recent Search item while already on this page), signal
      // the main effect to use replace so we don't create a back-button trap.
      if (q && !s && !o) {
        replaceNextNavigation.current = true
      }

      // Parse the new state from URL
      const newState = urlParamsToListingSearchState({
        allFilters,
        pageDefaultView,
        queryParams: getQueryParams(searchParams),
        storefront,
      })

      // myBids drives `id` from a live bid-status fetch (useMyBidsListingIdSync),
      // not the URL — it's deliberately never written there (see
      // listingSearchStateToUrlParams), so the parsed URL state always has
      // `id: null` here. Preserve whatever's currently in state instead of
      // clobbering the fetched ids every time the URL round-trips. Same
      // reasoning applies to (a) the watchlist bidStatus filter (id derived
      // by useWatchlistBidStatusSync) and (b) pages that opt out of writing
      // id to the URL entirely (Watchlist, where the id scope is implicit).
      const idIsDerived =
        newState.filters.myBids ||
        !!newState.filters.bidStatus ||
        !preserveIdInUrl
      const withMyBidsId = idIsDerived
        ? { ...newState, id: state.id }
        : newState

      // Freeze Sort (GLOB-4832) is likewise deliberately never encoded in
      // the URL (Req 5) — urlParamsToListingSearchState always parses it as
      // false. Preserve the live value across this round-trip too, same
      // reasoning as `id` above. Without this, any URL-driven update (e.g. a
      // pagination navigation, which does change the `p` param) would
      // silently clobber isSortFrozen back to false, even though pagination
      // is explicitly supposed to leave an active freeze in place (BP-2420) —
      // this isn't the "full navigation away" that Req 5 says should reset it.
      const partialState = { ...withMyBidsId, isSortFrozen: state.isSortFrozen }

      if (
        rememberedPageSize !== null &&
        rememberedPageSize !== partialState.pageSize
      ) {
        partialState.pageSize = rememberedPageSize
        // Writing `l` back is canonicalization, not a user action, so it must
        // not add a history entry that traps Back.
        replaceNextNavigation.current = true
      }

      // On initial mount, the URL simply reflects whatever was already there
      // (e.g. a reload) — it isn't the user applying a new query, so don't
      // treat it as one (that would strip a valid savedSearchId below).
      const applyingSearchQuery = !isInitialMount.current && q !== prevQ.current

      dispatch({
        type: 'SET_STATE',
        partialState,
        appliedFromSavedSearch: isAppliedFromSavedSearch.current,
        applyingSearchQuery,
      })
      isAppliedFromSavedSearch.current = false
      prevQ.current = q
    } else if (!isInitialMount.current) {
      // Reset state when URL goes from non-empty to empty (i.e. back button,
      // or the 'All Auctions' nav link while already on this page). This
      // must also reset `view` (dispatched first so the RESET_SORT below
      // picks the right default for it) -- otherwise a List-view toggle
      // survives the URL clearing back to defaults (BUGS-5517).
      dispatch({
        type: 'SET_VIEW',
        view: getView({ pageDefaultView, storefront }),
      })
      dispatch({ type: 'RESET_FILTERS' })
      dispatch({ type: 'RESET_SORT' })
      prevQ.current = null
    }
    if (
      !searchParams?.toString() &&
      rememberedPageSize !== null &&
      rememberedPageSize !== state.pageSize
    ) {
      replaceNextNavigation.current = true
      dispatch({ type: 'SET_PAGE_SIZE', pageSize: rememberedPageSize })
    }
    isInitialMount.current = false

    // We want this effect to trigger when the URL query parameters change, or
    // when a flag `getQueryParams` reads (enableMyBidsFilter or useBidCardTemplate
    // — the compound of watchlistMyBidsTemplate.enabled and inlineBidBoxEnabled)
    // resolves or changes — otherwise a flag that's still resolving (or streams a
    // later update) when this runs would strip a legitimately-set filter from the
    // URL and never get a chance to restore it, since nothing else re-parses
    // the (unchanged) URL afterward. If any of the other stuff we use in this
    // effect changes between renders, we don't care and don't need (or want)
    // to call it again.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [
    searchParamsString,
    enableMyBidsFilter,
    useBidCardTemplate,
    isPageSizeRemembered,
  ])

  // Rectify URL when search state is updated
  useEffect(() => {
    const urlParams = listingSearchStateToUrlParams({
      allFilters,
      pageDefaultView,
      state,
      storefront,
      preserveIdInUrl,
    })

    if (searchParams) {
      const propagatedQuery = Object.entries(getQueryParams(searchParams))
      const urlParamsEntries = Object.entries(urlParams)
      let updatedQuery = new URLSearchParams()

      propagatedQuery.forEach(([key, value]) => {
        if (!FILTER_KEYS.includes(key)) {
          /**
           * Remove search params if no longer in new urlParams.
           * Whitelist so that any non-search-related params are not lost.
           * Exception: always preserve 'id' parameter from current URL if it exists
           */
          if (isSupportedQueryParam(key) && !(key in urlParams)) {
            // Preserve 'id' parameter across page-size changes (deep-linked
            // allowlists from carousels etc). Opted out on pages where the
            // `id` scope is derived and implicit (e.g. Watchlist) so stale
            // URL ids can't leak back into the URL when we drop them.
            if (key === 'id' && preserveIdInUrl) {
              updatedQuery.set(key, value)
              return
            }
            return
          }
          updatedQuery.set(key, value)
        }
      })

      urlParamsEntries.forEach(([key, value]) => {
        updatedQuery.set(key, value)
      })

      if (savedSearchId) {
        const isApplyingFilters = FILTER_KEYS.some((key) => key in urlParams)

        // see if urlParams is missing FILTER_KEYS that are in searchParams
        const missingKeys = FILTER_KEYS.filter((key) => {
          return (
            searchParams.has(key) &&
            !urlParamsEntries.some(([urlKey]) => urlKey === key)
          )
        })

        // see if the remaining key values have changed
        const queryUnchanged = !isApplyingFilters
          ? true
          : urlParamsEntries.every(([key, value]) => {
              if (!FILTER_KEYS.includes(key)) return true
              return searchParams.get(key) === value
            }) && missingKeys.length === 0

        // see if all the filters have been removed
        const filtersWereCleared =
          !isApplyingFilters && FILTER_KEYS.some((key) => searchParams.has(key))

        if (
          (!state.appliedFromSavedSearch &&
            (state.applyingSearchQuery || !queryUnchanged)) ||
          filtersWereCleared
        ) {
          updatedQuery = deleteSavedSearchId(updatedQuery)
        }
      }

      const updatedQueryString = updatedQuery.toString().replace(/\+/g, '%20')
      const currentQueryString = searchParams.toString().replace(/\+/g, '%20')

      const sorted = (s: string) => s.split('&').sort().join('&')

      // The first run of this effect is the destination page's initial mount.
      // When we arrive with query params (e.g. a carousel "View More" URL), the
      // state was derived from that URL, so any difference here is pure
      // canonicalization (e.g. filling in a default sort param), not a user
      // action — canonicalizing via push would add a history entry that traps
      // the user on Back. Rectify it with replace instead. (An empty arrival
      // URL that later diverges is a genuine state-driven change → push.)
      const isArrivalCanonicalization =
        isInitialUrlSync.current && !!currentQueryString
      isInitialUrlSync.current = false

      // Only push/replace if the URL actually changed to avoid extra history entries.
      if (sorted(updatedQueryString) !== sorted(currentQueryString)) {
        // Use replace instead of push to avoid creating an intermediate history
        // entry that would trap the user when pressing the back button.
        if (replaceNextNavigation.current || isArrivalCanonicalization) {
          router.replace(`${pathname}?${updatedQueryString}`, { scroll: false })
          replaceNextNavigation.current = false
        } else {
          router.push(`${pathname}?${updatedQueryString}`, { scroll: false })
        }
      } else {
        // Reset so the next URL update doesn't look like a saved search.
        // Normally cleared in URL-to-state update, but not in this case.
        // Scenario: user creates a saved search (URL already matches), then
        // adds a text query — savedSearchId should still be stripped.
        isAppliedFromSavedSearch.current = false
      }
    }
    // Many aspects of state aren't truly stable.
    // Also, we only want this effect to run if the state changes, even
    // though we are using several other values as well.
    // Using `stableStringify(state)` as the deps gives us that.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [stableStringify(state)])

  useEffect(() => {
    if (currentSavedSearch) {
      const savedFilters = unformatFilters(currentSavedSearch)
      isAppliedFromSavedSearch.current = true
      replaceNextNavigation.current = true
      dispatch({
        type: 'SET_FILTERS',
        filters: savedFilters,
        appliedFromSavedSearch: true,
        applyingSearchQuery: !!savedFilters.q,
      })
    }
  }, [currentSavedSearch, dispatch])
}
